import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SEO from '../../shared/components/SEO';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import { LandingContainer, SectionTitle, SectionSubtitle } from '../home/HomePage.styles';
import '../home/HomePage.styles.css';
import { Footer } from '../../shared/components/Footer';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { FAQ_REPOSITORY } from '../../shared/services/faqChatService';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MemoryRounded from '@mui/icons-material/MemoryRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import TimerRounded from '@mui/icons-material/TimerRounded';
import HttpsRounded from '@mui/icons-material/HttpsRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import AppsRounded from '@mui/icons-material/AppsRounded';

const ACCENT = '#B83B1D';
const GREEN = '#1B7A4F';
const INK = '#2E140A';
const SECURITY_EMAIL = 'hi@re-stack.com';
const DOCS_MAILTO =
  'mailto:hi@re-stack.com?subject=Restack%20security%20documents%20(request%20access)&body=Hi%20Restack%2C%0A%0AWe%27re%20reviewing%20Restack%20and%20would%20like%20access%20to%20your%20security%20documentation%20(subprocessor%20list%2C%20DPA%2C%20architecture%20brief%2C%20SOC%202%2FISO%2027001%20progress).%0A%0ACompany%3A%0AName%3A%0A%0AThanks%2C%0A';

// Section anchors for the trust-center sub-nav.
const NAV = [
  { id: 'overview', label: 'Overview' },
  { id: 'data', label: 'Data handling' },
  { id: 'controls', label: 'Controls' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'documents', label: 'Documents' },
  { id: 'faq', label: 'FAQ' },
  { id: 'resources', label: 'Resources' },
];

// Compliance posture. Honest statuses only. GDPR and CCPA alignment is a
// description of current practice, not a certificate. No SOC 2 or ISO 27001
// certificate is held today, so the badge says exactly that and the note says
// where each program stands. The status wording must never be read as a claim
// of certification.
const COMPLIANCE = [
  { label: 'GDPR', status: 'Aligned', note: 'Current practice. Not a certification.', tone: 'now' },
  { label: 'CCPA', status: 'Aligned', note: 'Current practice. Not a certification.', tone: 'now' },
  { label: 'SOC 2', status: 'Not certified yet', note: 'Program in progress. Type I is the next milestone, Type II after it.', tone: 'progress' },
  { label: 'ISO 27001', status: 'Not certified yet', note: 'Program in progress. No certificate held today.', tone: 'progress' },
];

// Controls catalog — grouped like an enterprise trust center, but every line
// is something demonstrable in a technical review. No claimed certifications.
const CONTROL_GROUPS = [
  {
    title: 'Data protection',
    items: [
      'TLS 1.2+ (1.3 supported) in transit; AES-256 encryption at rest',
      'No model training on customer data (Anthropic commercial API terms)',
      'Live Office files remain in Microsoft 365; source uploads and session data use encrypted Restack storage',
      'Firm-level data isolation enforced by security rules',
      'No standalone server-side copy of the live Office file',
    ],
  },
  {
    title: 'Identity & access',
    items: [
      'Microsoft Entra ID sign-in and Office add-in SSO',
      'Organization roles checked server-side on privileged actions',
      'Least-privilege internal access with periodic reviews',
      'Centralized Microsoft 365 deployment scoped to admin-chosen groups',
    ],
  },
  {
    title: 'Application security',
    items: [
      'AI-generated code runs in a locked-down SES sandbox (frozen intrinsics)',
      'Dangerous runtime APIs blocked (Function / Reflect / Proxy, network)',
      'File content treated as untrusted (prompt-injection defenses)',
      'File access only through Office.js, no arbitrary file or OS access',
      'Secure development lifecycle with peer code review',
      'Automated dependency and code scanning (Dependabot and CodeQL)',
      'Link-scheme allowlist and security response headers (HSTS, CSP)',
    ],
  },
  {
    title: 'Infrastructure & network',
    items: [
      'Hosted on Google Cloud (Cloud Run) and Firebase',
      'TLS 1.2+ (1.3 supported) everywhere; private service configuration',
      'Platform-level DDoS protection and network controls',
      'Audit logging of administrative and data events',
      'Default-deny Firestore and Storage access rules',
    ],
  },
  {
    title: 'Corporate & governance',
    items: [
      'Information-security policies being formalized (SOC 2 Type I next; Type II and ISO 27001 on the roadmap)',
      'Subprocessor security review before onboarding',
      'Commitment to notify affected customers of confirmed security incidents',
      'Confidentiality agreements for all personnel',
      'Security awareness across the team',
    ],
  },
  {
    title: 'Practice contract review',
    accent: true,
    items: [
      'Zero Data Retention requires written provider and customer confirmation',
      'Custom DPA and customer-specific compliance terms',
      'Dedicated, regional, and customer-managed-key requirements require a separate feasibility review',
    ],
  },
];

// The ten questions a security or procurement reviewer asks first. The first
// answer is pulled by id from FAQ_REPOSITORY so this page and the support
// widget give the same reply; the rest restate what the sections below already
// say, so the FAQ never carries a claim the page does not otherwise make.
const REPO_SECURITY_FAQ = FAQ_REPOSITORY.find((entry) => entry.id === 'security');
const SECURITY_FAQ = [
  ...(REPO_SECURITY_FAQ ? [{ q: REPO_SECURITY_FAQ.question, a: REPO_SECURITY_FAQ.answer }] : []),
  {
    q: 'Where do our files actually live?',
    a: 'The live PowerPoint, Excel, and Word files stay in Microsoft 365. Restack reads the Office content a request needs and writes back through Office.js, without creating a standalone server-side copy of the live file. Chat and session records, plus source files you upload, are held in encrypted user- and chat-scoped Restack storage so work can run and resume.',
  },
  {
    q: 'Do you hold SOC 2 or ISO 27001?',
    a: 'No. We hold neither certificate today. Both programs are in progress, with SOC 2 Type I as the next milestone and Type II after it. GDPR and CCPA alignment describes our current practice rather than a certification. We would rather be precise than impressive, so this page will say so plainly until that changes.',
  },
  {
    q: 'Is our data separated from other firms?',
    a: 'Yes, and structurally. Privileged functions resolve your firm from the caller’s verified identity, never from a request parameter, so cross-firm access cannot be expressed rather than merely being blocked. Firestore and Storage access rules are default-deny.',
  },
  {
    q: 'Do you train models on our prompts or documents?',
    a: 'No. Prompts and responses run under Anthropic’s commercial API terms, which prohibit training on them. Zero Data Retention is represented only after provider eligibility and written contract confirmation.',
  },
  {
    q: 'What stops a hostile document from steering the agent?',
    a: 'File content is treated as untrusted, with prompt-injection defenses that keep instructions hidden inside a file from steering the run. Any AI-generated code runs in a locked-down SES compartment that fails closed if the sandbox is unavailable: no network, no Function, Reflect, Proxy, or eval, and blocked Office APIs enforced at the capability layer.',
  },
  {
    q: 'How does IT deploy Restack across our tenant?',
    a: 'From the Microsoft 365 Admin Center. IT pushes the add-in to specific Azure AD groups, one manifest covers PowerPoint, Excel, and Word, and there is no Office Store step and no installed software. Sign-in uses a Microsoft work account, with Office 365 SSO where the host provides it.',
  },
  {
    q: 'Can we get a custom DPA or Zero Data Retention?',
    a: 'Those are reviewed during Practice contracting rather than promised up front. Zero Data Retention requires written provider and customer confirmation. Custom DPA and customer-specific compliance terms are in scope; dedicated, regional, and customer-managed-key requirements need a separate feasibility review first.',
  },
  {
    q: 'How do we get the subprocessor list and DPA?',
    a: 'Email hi@re-stack.com. We maintain a current subprocessor list, our Data Processing Agreement, the full architecture brief, and our SOC 2 and ISO 27001 program status, shared with customers and active prospects under NDA. We respond within one business day.',
  },
  {
    q: 'How do we report a vulnerability?',
    a: 'Email hi@re-stack.com. We acknowledge reports within one business day, and we commit to notifying affected customers of confirmed security incidents.',
  },
];

// FAQPage structured data, shipped through SEO.js into <head>.
const SECURITY_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: SECURITY_FAQ.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

export default function SecurityPage() {
  const hair = alpha(INK, 0.08);

  const sectionHeadingSx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: { xs: '1.05rem', md: '1.15rem' },
    fontWeight: 600,
    letterSpacing: '-0.015em',
    color: 'text.primary',
  };
  const bodySx = {
    fontFamily: BRAND_SANS_FONT,
    fontSize: '0.84rem',
    fontWeight: 400,
    lineHeight: 1.6,
    color: 'text.secondary',
  };
  const focusRing = {
    '&:focus-visible': {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 3,
    },
  };
  const linkSx = {
    color: ACCENT,
    textDecorationColor: alpha(ACCENT, 0.4),
    textUnderlineOffset: '0.18em',
    fontWeight: 500,
    borderRadius: '4px',
    '&:hover': { textDecorationColor: ACCENT },
    ...focusRing,
  };
  const primaryBtn = {
    textTransform: 'none',
    fontFamily: BRAND_SANS_FONT,
    fontWeight: 600,
    fontSize: '0.85rem',
    letterSpacing: '-0.005em',
    borderRadius: '999px',
    px: 2.5,
    py: 0.9,
    color: '#FFFFFF',
    background: 'linear-gradient(135deg, #B83B1D 0%, #D4694E 100%)',
    boxShadow: '0 4px 14px rgba(184, 59, 29, 0.22)',
    transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #A63419 0%, #C25A42 100%)',
      boxShadow: '0 6px 18px rgba(184, 59, 29, 0.3)',
    },
    '&:active': { transform: 'translateY(1px)', boxShadow: '0 2px 8px rgba(184, 59, 29, 0.24)' },
    ...focusRing,
  };
  const secondaryBtn = {
    textTransform: 'none',
    fontFamily: BRAND_SANS_FONT,
    fontWeight: 500,
    fontSize: '0.85rem',
    letterSpacing: '-0.005em',
    borderRadius: '999px',
    px: 2.5,
    py: 0.9,
    color: 'text.primary',
    border: `1px solid ${alpha(INK, 0.2)}`,
    transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.12s ease',
    '&:hover': { backgroundColor: alpha(INK, 0.04), borderColor: alpha(INK, 0.32) },
    '&:active': { transform: 'translateY(1px)' },
    ...focusRing,
  };

  // Anchor jump that also moves real focus, so a keyboard user lands inside the
  // section rather than back at the top of the tab order. Honours
  // prefers-reduced-motion.
  const goTo = (id) => (e) => {
    const el = document.getElementById(id);
    if (!el) return; // leave the default fragment behaviour alone
    e.preventDefault();
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  };

  // Bulleted control item (rust dot).
  const ControlItem = ({ children, accent }) => (
    <Box
      component="li"
      sx={{
        position: 'relative',
        pl: '1.05rem',
        fontFamily: BRAND_SANS_FONT,
        fontSize: '0.84rem',
        lineHeight: 1.55,
        color: 'text.secondary',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '0.55em',
          width: 5,
          height: 5,
          borderRadius: '50%',
          backgroundColor: accent ? ACCENT : GREEN,
        },
      }}
    >
      {children}
    </Box>
  );

  // One rhythm for every section on the page: same top margin, same top
  // padding, same hairline, same scroll offset, same intro measure. No section
  // sets its own spacing.
  const SecSection = ({ id, title, intro, children }) => (
    <Box
      component="section"
      id={id}
      aria-labelledby={`${id}-h`}
      sx={{
        scrollMarginTop: { xs: '76px', md: '92px' },
        mt: { xs: 5, md: 7 },
        pt: { xs: 4, md: 5 },
        borderTop: `1px solid ${hair}`,
        '&:focus': { outline: 'none' },
      }}
    >
      <Typography component="h2" id={`${id}-h`} sx={sectionHeadingSx}>
        {title}
      </Typography>
      {intro && (
        <Typography sx={{ ...bodySx, mt: 1, maxWidth: 760 }}>{intro}</Typography>
      )}
      <Box sx={{ mt: 2.5 }}>{children}</Box>
    </Box>
  );

  return (
    <LandingContainer data-theme="light" sx={{ minHeight: '100vh' }}>
      <SEO
        title="Trust Center"
        description="Restack Trust Center: live Office files remain in Microsoft 365, while source uploads and session data use encrypted Restack storage. Structural tenant isolation, a fail-closed sandbox, no-training commitments, and our SOC 2 and ISO 27001 status."
        canonical="/security"
        schema={SECURITY_FAQ_SCHEMA}
      />

      <SiteHeader />

      <Box
        component="main"
        id="main-content"
        sx={{
          maxWidth: 1080,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 'calc(56px + 1.5rem)', md: 'calc(64px + 2.5rem)' },
          pb: { xs: 6, md: 9 },
          '& strong': { color: 'text.primary', fontWeight: 600 },
        }}
      >
        {/* Hero */}
        <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
          <Typography
            component="div"
            sx={{
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.68rem',
              fontWeight: 600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: ACCENT,
              mb: 1.5,
            }}
          >
            Trust Center
          </Typography>
          <SectionTitle component="h1" sx={{ mb: 2, textAlign: 'center' }}>
            Your live Office files <em>stay in Microsoft 365.</em>
          </SectionTitle>
          {/* The short answer. A reviewer who reads nothing else should still
              leave knowing where the files sit and what we store. Everything
              this used to carry (structural isolation, the fail-closed
              sandbox, the no-training terms, the written-confirmation rule) is
              stated in full in the sections below, so nothing is lost by
              keeping the top of the page to three sentences. */}
          <SectionSubtitle sx={{ mx: 'auto', mb: { xs: 2.5, md: 3 }, maxWidth: { xs: '100%', md: 820 } }}>
            Restack works against the slides, workbooks, and documents open in Microsoft
            365 without creating a standalone server-side copy of the live Office file.
            Chat and session records, plus source files you upload, use encrypted Restack
            storage so work can run and resume. Everything below is demonstrable in a
            technical review, and nothing on this page implies a certification we do not
            hold.
          </SectionSubtitle>

          {/* Hero proof chips: the data model at a glance */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'center',
              mb: { xs: 3, md: 3.5 },
            }}
          >
            {[
              { Icon: TimerRounded, label: 'Live files stay in Office' },
              { Icon: LockRounded, label: 'Isolation is structural' },
              { Icon: MemoryRounded, label: 'Fail-closed sandbox' },
              { Icon: ShieldRounded, label: 'No model training' },
              { Icon: HttpsRounded, label: 'Encrypted at rest' },
            ].map(({ Icon, label }) => (
              <Box
                key={label}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.7,
                  px: 1.4,
                  py: 0.65,
                  borderRadius: '999px',
                  border: `1px solid ${alpha(ACCENT, 0.22)}`,
                  backgroundColor: alpha(ACCENT, 0.05),
                }}
              >
                <Icon aria-hidden="true" sx={{ fontSize: 15, color: ACCENT }} />
                <Box
                  component="span"
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    letterSpacing: '-0.005em',
                    color: 'text.primary',
                  }}
                >
                  {label}
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, justifyContent: 'center' }}>
            <Button
              component="a"
              href="/Restack-Security-Brief.pdf"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<DownloadRounded sx={{ fontSize: '1.05rem' }} />}
              sx={primaryBtn}
            >
              Download security brief
            </Button>
            <Button
              component="a"
              href={DOCS_MAILTO}
              startIcon={<LockOutlined sx={{ fontSize: '1.05rem' }} />}
              sx={secondaryBtn}
            >
              Request documents
            </Button>
          </Box>
          <Typography
            component="div"
            sx={{
              mt: 2,
              fontFamily: BRAND_SANS_FONT,
              fontSize: '0.72rem',
              fontWeight: 500,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: alpha(INK, 0.45),
            }}
          >
            Version 1.0 &middot; May 2026
          </Typography>
        </Box>

        {/* Sub-nav */}
        <Box
          component="nav"
          aria-label="Trust Center sections"
          sx={{
            mt: { xs: 3.5, md: 4.5 },
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
          }}
        >
          {NAV.map((n) => (
            <Box
              key={n.id}
              component="a"
              href={`#${n.id}`}
              onClick={goTo(n.id)}
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'text.secondary',
                textDecoration: 'none',
                px: 1.5,
                py: 0.6,
                borderRadius: '999px',
                border: `1px solid ${hair}`,
                cursor: 'pointer',
                transition: 'color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease, transform 0.12s ease',
                '&:hover': { color: ACCENT, borderColor: alpha(ACCENT, 0.3), backgroundColor: alpha(ACCENT, 0.04) },
                '&:active': { transform: 'translateY(1px)' },
                ...focusRing,
              }}
            >
              {n.label}
            </Box>
          ))}
        </Box>

        {/* OVERVIEW — lead trust claim */}
        <SecSection
          id="overview"
          title="Overview"
          intro="Restack runs server-side and edits content directly in your open PowerPoint, Excel, and Word files. The live Office files remain in Microsoft 365. Restack stores chat and session records, plus source files you upload, in encrypted user- and chat-scoped storage so work can run and resume."
        >
          <Box
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: '16px',
              border: `1px solid ${alpha(ACCENT, 0.18)}`,
              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.055)} 0%, ${alpha('#D4694E', 0.03)} 100%)`,
              maxWidth: 720,
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #B83B1D 0%, #D4694E 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ShieldRounded aria-hidden="true" sx={{ fontSize: 18, color: '#FAF9F7' }} />
            </Box>
            <Box>
              <Typography
                component="h3"
                sx={{
                  fontFamily: BRAND_SANS_FONT,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  color: 'text.primary',
                  mb: 0.6,
                }}
              >
                We never train on your data.
              </Typography>
              <Typography component="div" sx={{ ...bodySx, lineHeight: 1.6 }}>
                Prompts and responses run under Anthropic&rsquo;s commercial API terms,
                which prohibit training on them. Zero Data Retention is represented
                only after provider eligibility and written contract confirmation.
              </Typography>
            </Box>
          </Box>
        </SecSection>

        {/* DATA HANDLING — principle cards */}
        <SecSection
          id="data"
          title="How your data is handled"
          intro="The principles that govern every request. Full architecture and the Microsoft 365 deployment steps are in the brief."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {[
              { Icon: MemoryRounded, title: 'Fail-closed sandbox', body: 'AI-generated code runs in a locked-down SES compartment that fails closed if unavailable. No network, no Function / Reflect / Proxy / eval, and blocked Office APIs enforced at the capability layer.' },
              { Icon: ShieldRounded, title: 'File content treated as untrusted', body: 'Instructions hidden inside a file cannot hijack the analyst. Prompt-injection defenses keep file content from steering the run.' },
              { Icon: LockRounded, title: 'Structural firm isolation', body: 'Privileged functions resolve your firm from the caller’s verified identity, never from a request parameter, so cross-firm access is impossible to express, not just blocked.' },
              { Icon: TimerRounded, title: 'Live Office files stay in Microsoft 365', body: 'Restack reads the Office content needed for the request without storing a standalone copy of the live file. Chat and session records, plus source files you upload, may use encrypted Restack storage so work can resume.' },
              { Icon: HttpsRounded, title: 'Encrypted at rest and in transit', body: 'AES-256 at rest with Google-managed keys and TLS 1.2+ in transit, with TLS 1.3 supported.' },
              { Icon: KeyRounded, title: 'Microsoft Entra ID sign-in', body: 'Sign in with a Microsoft work account. The Office add-in uses Microsoft 365 SSO where the host provides it.' },
              { Icon: AppsRounded, title: 'Centralized deployment', body: 'IT pushes the add-in to specific Azure AD groups from the Microsoft 365 Admin Center. One manifest covers all three apps, no Office Store, no installed software.' },
            ].map(({ Icon, title, body }) => (
              <Box
                key={title}
                sx={{
                  p: { xs: 2.25, md: 2.5 },
                  borderRadius: '16px',
                  border: `1px solid ${alpha(INK, 0.09)}`,
                  backgroundColor: alpha(INK, 0.015),
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${alpha(ACCENT, 0.2)}`,
                    backgroundColor: alpha(ACCENT, 0.06),
                    color: ACCENT,
                    mb: 1.5,
                  }}
                >
                  <Icon aria-hidden="true" sx={{ fontSize: 18 }} />
                </Box>
                <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.92rem', fontWeight: 600, letterSpacing: '-0.01em', color: 'text.primary', mb: 0.5 }}>
                  {title}
                </Typography>
                <Typography component="div" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.82rem', lineHeight: 1.55, color: 'text.secondary' }}>
                  {body}
                </Typography>
              </Box>
            ))}
          </Box>
        </SecSection>

        {/* CONTROLS — categorized catalog */}
        <SecSection
          id="controls"
          title="Controls"
          intro="What is in place today, grouped the way a security review reads. Every line is demonstrable; nothing here implies a certification we do not hold."
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {CONTROL_GROUPS.map((g) => (
              <Box
                key={g.title}
                sx={{
                  p: { xs: 2.5, md: 2.75 },
                  borderRadius: '16px',
                  border: `1px solid ${g.accent ? alpha(ACCENT, 0.22) : alpha(INK, 0.09)}`,
                  backgroundColor: g.accent ? alpha(ACCENT, 0.035) : alpha(INK, 0.015),
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: BRAND_SANS_FONT,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: g.accent ? ACCENT : alpha(INK, 0.55),
                    mb: 1.5,
                  }}
                >
                  {g.title}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                  {g.items.map((item) => (
                    <ControlItem key={item} accent={g.accent}>{item}</ControlItem>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </SecSection>

        {/* COMPLIANCE. Badges. Each card states the status AND what that
            status is not, so a badge can never be skimmed as a certificate. */}
        <SecSection
          id="compliance"
          title="Compliance"
          intro="We would rather be precise than impressive. GDPR and CCPA alignment describes our current practice and is in effect today. We hold no SOC 2 or ISO 27001 certificate; both programs are in progress."
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 1.5, md: 2 } }}>
            {COMPLIANCE.map((c) => {
              const color = c.tone === 'now' ? GREEN : alpha(INK, 0.5);
              return (
                <Box
                  key={c.label}
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    border: `1px solid ${alpha(INK, 0.1)}`,
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                  }}
                >
                  <ShieldRounded aria-hidden="true" sx={{ fontSize: 22, color, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.9rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                      {c.label}
                    </Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                      <Box aria-hidden="true" sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: color }} />
                      <Box component="span" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.72rem', fontWeight: 500, color }}>
                        {c.status}
                      </Box>
                    </Box>
                    <Typography component="div" sx={{ ...bodySx, fontSize: '0.78rem', lineHeight: 1.5, mt: 0.5 }}>
                      {c.note}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </SecSection>

        {/* DOCUMENTS — request access (subprocessors gated) */}
        <SecSection
          id="documents"
          title="Security documents"
          intro="We maintain a current subprocessor list, our Data Processing Agreement, the full architecture brief, and our SOC 2 and ISO 27001 program status. These are shared with customers and active prospects under NDA."
        >
          <Box
            sx={{
              p: { xs: 2.75, md: 3.5 },
              borderRadius: '16px',
              border: `1px solid ${alpha(INK, 0.1)}`,
              background: `linear-gradient(135deg, ${alpha(INK, 0.02)} 0%, ${alpha(ACCENT, 0.03)} 100%)`,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ maxWidth: 560 }}>
              <Typography component="h3" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.95rem', fontWeight: 600, color: 'text.primary', mb: 0.6 }}>
                Request access to our security package
              </Typography>
              <Typography sx={bodySx}>
                Subprocessor list, DPA, architecture brief, and where each compliance
                program stands. We respond within one business day.
              </Typography>
            </Box>
            <Button
              component="a"
              href={DOCS_MAILTO}
              endIcon={<ArrowForwardRounded sx={{ fontSize: '1rem' }} />}
              sx={{ ...primaryBtn, flexShrink: 0 }}
            >
              Request access
            </Button>
          </Box>
        </SecSection>

        {/* FAQ. The questions a security review opens with. The first answer
            comes straight from FAQ_REPOSITORY so the page and the support
            widget never drift; the rest restate the sections above. Emitted as
            FAQPage JSON-LD via the SEO schema. */}
        <SecSection
          id="faq"
          title="Questions a security review opens with"
          intro="Short answers to what procurement and IT ask first. Every one of them is backed by a section above."
        >
          <Box sx={{ maxWidth: 820 }}>
            {SECURITY_FAQ.map(({ q, a }) => (
              <Accordion
                key={q}
                disableGutters
                elevation={0}
                square
                sx={{
                  backgroundColor: 'transparent',
                  borderBottom: `1px solid ${alpha(INK, 0.08)}`,
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRounded sx={{ color: alpha(INK, 0.45) }} />}
                  sx={{
                    px: 0.5,
                    minHeight: 52,
                    borderRadius: '8px',
                    '& .MuiAccordionSummary-content': { my: 1 },
                    '&:hover': { backgroundColor: alpha(INK, 0.02) },
                    ...focusRing,
                  }}
                >
                  {/* MUI v7 already wraps the summary in its own heading, so
                      this stays a span to avoid nesting a heading in a heading. */}
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: BRAND_SANS_FONT,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                    }}
                  >
                    {q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2 }}>
                  <Typography sx={{ ...bodySx, lineHeight: 1.65 }}>{a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </SecSection>

        {/* RESOURCES */}
        <SecSection id="resources" title="Resources">
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[
              { label: 'Security brief (PDF)', note: 'Public download', href: '/Restack-Security-Brief.pdf', external: true },
              { label: 'Privacy Policy', note: 'How we handle data', href: '/privacy' },
              { label: 'Terms of Service', note: 'Rules for using Restack', href: '/terms' },
              { label: 'Subprocessor list & DPA', note: 'Available on request', href: DOCS_MAILTO },
            ].map((r) => (
              <Box
                key={r.label}
                component="a"
                href={r.href}
                {...(r.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  py: 1.25,
                  px: 1.5,
                  borderRadius: '12px',
                  border: `1px solid ${hair}`,
                  textDecoration: 'none',
                  transition: 'border-color 0.2s ease, background-color 0.2s ease, transform 0.12s ease',
                  '&:hover': { borderColor: alpha(ACCENT, 0.3), backgroundColor: alpha(ACCENT, 0.03) },
                  '&:active': { transform: 'translateY(1px)' },
                  ...focusRing,
                }}
              >
                <Box>
                  <Box component="span" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.88rem', fontWeight: 500, color: 'text.primary' }}>
                    {r.label}
                  </Box>
                  <Box component="span" sx={{ fontFamily: BRAND_SANS_FONT, fontSize: '0.78rem', color: alpha(INK, 0.5), ml: 1 }}>
                    {r.note}
                  </Box>
                </Box>
                <ArrowForwardRounded aria-hidden="true" sx={{ fontSize: '1rem', color: ACCENT }} />
              </Box>
            ))}
          </Box>

          <Typography sx={{ ...bodySx, mt: 3 }}>
            Security questions or to report a vulnerability, contact{' '}
            <Link href={`mailto:${SECURITY_EMAIL}`} sx={linkSx}>{SECURITY_EMAIL}</Link>.
            We acknowledge reports within one business day.
          </Typography>
        </SecSection>

        {/* CLOSING CTA. The same two actions as the hero, repeated verbatim,
            so a reviewer who reads to the bottom does not have to scroll back. */}
        <SecSection
          id="get-the-pack"
          title="Take the pack to your review"
          intro="The brief covers the architecture, the data flows, and the Microsoft 365 deployment steps. Anything it does not answer, ask us directly."
        >
          <Box
            sx={{
              p: { xs: 2.75, md: 3.5 },
              borderRadius: '16px',
              border: `1px solid ${alpha(INK, 0.1)}`,
              background: `linear-gradient(135deg, ${alpha(ACCENT, 0.05)} 0%, ${alpha('#FAF9F7', 0.6)} 100%)`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.25,
            }}
          >
            <Button
              component="a"
              href="/Restack-Security-Brief.pdf"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<DownloadRounded sx={{ fontSize: '1.05rem' }} />}
              sx={primaryBtn}
            >
              Download security brief
            </Button>
            <Button
              component="a"
              href={DOCS_MAILTO}
              startIcon={<LockOutlined sx={{ fontSize: '1.05rem' }} />}
              sx={secondaryBtn}
            >
              Request documents
            </Button>
          </Box>
        </SecSection>
      </Box>

      <Footer />
    </LandingContainer>
  );
}
